import { useEffect, useRef, useState, useCallback } from 'react';

export default function BarcodeScanner({ onDetected, onClose }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const animFrameRef = useRef(null);
  const detectedRef = useRef(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const stopAll = useCallback(() => {
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
  }, []);

  const handleDetected = useCallback((code) => {
    if (detectedRef.current) return;
    detectedRef.current = true;
    if (navigator.vibrate) navigator.vibrate(100);
    stopAll();
    onDetected(code);
  }, [onDetected, stopAll]);

  useEffect(() => {
    let active = true;

    const start = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } }
        });

        if (!active) { stream.getTracks().forEach(t => t.stop()); return; }
        streamRef.current = stream;

        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.setAttribute('playsinline', 'true');
          await videoRef.current.play();
        }

        setLoading(false);

        // Use native BarcodeDetector (Chrome 83+, Android Chrome, Edge)
        if ('BarcodeDetector' in window) {
          const detector = new window.BarcodeDetector({
            formats: ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128', 'qr_code'],
          });

          const scan = async () => {
            if (!active || detectedRef.current || !videoRef.current) return;
            if (videoRef.current.readyState === 4) {
              try {
                const results = await detector.detect(videoRef.current);
                if (results.length > 0) { handleDetected(results[0].rawValue); return; }
              } catch (e) { /* no result */ }
            }
            animFrameRef.current = requestAnimationFrame(scan);
          };
          animFrameRef.current = requestAnimationFrame(scan);

        } else {
          // Fallback for browsers without BarcodeDetector: use canvas + ZXing CDN
          const script = document.createElement('script');
          script.src = 'https://cdn.jsdelivr.net/npm/@zxing/browser@0.1.5/umd/index.min.js';
          script.onload = () => {
            if (!active || detectedRef.current) return;
            try {
              const reader = new window.ZXingBrowser.BrowserMultiFormatReader();
              reader.decodeFromVideoDevice(null, videoRef.current, (result, err) => {
                if (result && active && !detectedRef.current) {
                  handleDetected(result.getText());
                }
              });
            } catch (e) {
              console.warn('ZXing fallback niet beschikbaar:', e);
            }
          };
          script.onerror = () => console.warn('ZXing CDN niet bereikbaar');
          document.head.appendChild(script);
        }

      } catch (err) {
        if (!active) return;
        setLoading(false);
        if (err.name === 'NotAllowedError') {
          setError('Camera toestemming geweigerd. Sta camera toe voor dit domein in je browserinstellingen.');
        } else if (err.name === 'NotFoundError') {
          setError('Geen camera gevonden op dit apparaat.');
        } else {
          setError('Camera kon niet worden gestart. Probeer de pagina te herladen.');
        }
      }
    };

    start();
    return () => { active = false; stopAll(); };
  }, [handleDetected, stopAll]);

  const [torchOn, setTorchOn] = useState(false);
  const toggleTorch = async () => {
    const track = streamRef.current?.getVideoTracks()[0];
    if (!track) return;
    try {
      await track.applyConstraints({ advanced: [{ torch: !torchOn }] });
      setTorchOn(t => !t);
    } catch (e) { /* not supported */ }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: '#000', zIndex: 500, display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '16px', paddingTop: 'calc(16px + env(safe-area-inset-top, 0))', background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
        <button onClick={() => { stopAll(); onClose(); }} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', borderRadius: '50%', width: 38, height: 38, fontSize: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
        <span style={{ color: '#fff', fontFamily: 'var(--mono)', fontSize: 13, flex: 1, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Barcode scannen</span>
        <button onClick={toggleTorch} style={{ background: torchOn ? 'var(--ice)' : 'rgba(255,255,255,0.15)', border: 'none', color: torchOn ? '#000' : '#fff', borderRadius: '50%', width: 38, height: 38, fontSize: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>🔦</button>
      </div>

      <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
        <video ref={videoRef} style={{ width: '100%', height: '100%', objectFit: 'cover' }} muted playsInline autoPlay />
        <canvas ref={canvasRef} style={{ display: 'none' }} />

        {!error && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)' }} />
            <div style={{ position: 'relative', width: '78%', maxWidth: 320, height: 160, zIndex: 2 }}>
              <div style={{ position: 'absolute', inset: 0, boxShadow: '0 0 0 9999px rgba(0,0,0,0.45)', borderRadius: 8 }} />
              {[
                { top: 0, left: 0, borderTop: '3px solid var(--ice)', borderLeft: '3px solid var(--ice)', borderRadius: '6px 0 0 0' },
                { top: 0, right: 0, borderTop: '3px solid var(--ice)', borderRight: '3px solid var(--ice)', borderRadius: '0 6px 0 0' },
                { bottom: 0, left: 0, borderBottom: '3px solid var(--ice)', borderLeft: '3px solid var(--ice)', borderRadius: '0 0 0 6px' },
                { bottom: 0, right: 0, borderBottom: '3px solid var(--ice)', borderRight: '3px solid var(--ice)', borderRadius: '0 0 6px 0' },
              ].map((s, i) => <div key={i} style={{ position: 'absolute', width: 24, height: 24, ...s }} />)}
              {!loading && <div style={{ position: 'absolute', left: 4, right: 4, height: 2, background: 'linear-gradient(90deg, transparent, var(--ice), transparent)', animation: 'scanLine 2s ease-in-out infinite', top: '50%' }} />}
            </div>
            <div style={{ position: 'absolute', bottom: '20%', left: 0, right: 0, textAlign: 'center', color: 'rgba(255,255,255,0.75)', fontSize: 13 }}>
              {loading ? 'Camera starten...' : 'Houd de barcode in het kader'}
            </div>
          </div>
        )}

        {loading && !error && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 5 }}>
            <div className="loading-spinner" style={{ width: 36, height: 36, borderWidth: 3 }} />
          </div>
        )}

        {error && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 32, zIndex: 5 }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>📵</div>
            <p style={{ color: '#fff', textAlign: 'center', marginBottom: 24, fontSize: 14, lineHeight: 1.6 }}>{error}</p>
            <button className="btn btn-primary" onClick={onClose}>Sluiten</button>
          </div>
        )}
      </div>

      <style>{`@keyframes scanLine { 0%{top:10%} 50%{top:85%} 100%{top:10%} }`}</style>
    </div>
  );
}
